import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StyledAppMobileNav } from './AppMobileNav.styles';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { RoutePaths } from 'config/route-paths';
import {
  selectHasAccessToGCD,
  selectHasAccessToRobots,
  selectUserCountryCode,
} from 'app/cross-cutting-concerns/authentication/state/authenticationSelectors';

interface AppMobileNavProps {
  open: boolean;
  onClose: () => void;
}

const getMobileNavItemClassName = ({ isActive }: { isActive: boolean }): string =>
  isActive ? 'mobile-nav__item active' : 'mobile-nav__item';

export const AppMobileNav = ({ open, onClose }: AppMobileNavProps): JSX.Element => {
  const { t, i18n } = useTranslation();
  const haveRobots = useSelector(selectHasAccessToRobots);
  const haveGCD = useSelector(selectHasAccessToGCD);
  const countryCode = useSelector(selectUserCountryCode);

  const showMachineDashboard = !haveRobots || haveGCD;
  const showRobotDashboard = haveRobots;

  const countryName = ((): string => {
    try {
      return new Intl.DisplayNames([i18n.language], { type: 'region' }).of(countryCode) ?? countryCode;
    } catch {
      return countryCode;
    }
  })();

  const languageCode = i18n.language.split('-')[0].toUpperCase();

  return (
    <StyledAppMobileNav $open={open}>
      <button type="button" className="mobile-nav__backdrop" onClick={onClose} aria-label="Close navigation" />
      <div className="mobile-nav__panel">
        <div className="mobile-nav__header">
          <div className="mobile-nav__header-spacer" />
          <Link to={RoutePaths.ROOT} onClick={onClose} className="mobile-nav__logo-link">
            <SvgIcon name="karcherLogo" className="mobile-nav__logo" />
          </Link>
          <button type="button" className="mobile-nav__close" onClick={onClose} aria-label="Close navigation">
            <SvgIcon name="cancel" className="mobile-nav__close-icon" />
          </button>
        </div>

        <div className="mobile-nav__body">
          {showMachineDashboard && (
            <NavLink to={RoutePaths.MACHINE_DASHBOARD} className={getMobileNavItemClassName} onClick={onClose}>
              {t('common.machineDashboard')}
            </NavLink>
          )}
          {showRobotDashboard && (
            <NavLink to={RoutePaths.ROBOT_DASHBOARD} className={getMobileNavItemClassName} onClick={onClose}>
              {t('common.robotDashboard')}
            </NavLink>
          )}
          <NavLink to={RoutePaths.MACHINES} className={getMobileNavItemClassName} onClick={onClose}>
            {t('common.devices')}
          </NavLink>
          <NavLink to={RoutePaths.EVENTS} className={getMobileNavItemClassName} onClick={onClose}>
            {t('common.reports')}
          </NavLink>
          <NavLink to={RoutePaths.NOTIFICATIONS} className={getMobileNavItemClassName} onClick={onClose}>
            {t('common.notifications')}
          </NavLink>
          <NavLink to={RoutePaths.SITES} className={getMobileNavItemClassName} onClick={onClose}>
            {t('common.sites')}
          </NavLink>
          <NavLink to={RoutePaths.USERS} className={getMobileNavItemClassName} onClick={onClose}>
            {t('common.userManagement')}
          </NavLink>
        </div>

        <div className="mobile-nav__footer">
          <SvgIcon name="global" className="mobile-nav__globe" />
          <span className="mobile-nav__language">
            {countryName} | {languageCode}
          </span>
        </div>
      </div>
    </StyledAppMobileNav>
  );
};
